import React from 'react';
import clsx from 'clsx';
import './FolderCountBadge.scss';

export interface IFolderCountBadgeProps {
    /** Значение счётчика. undefined = нет данных (возможно ошибка/первичная загрузка). */
    count?: number;
    /** Если true и count === undefined — рендерится skeleton-точка. */
    loading?: boolean;
    /** Порог отображения: count > max → "{max}+". Default 99. */
    max?: number;
    /** Дополнительный className. */
    className?: string;
}

const DEFAULT_MAX = 99;

/**
 * Бейдж-счётчик для пункта меню Задачи/Документы. Простой pill-компонент,
 * без анимации цифр (в отличие от NotificationBadge в Navbar — там смена значения
 * заметнее, а здесь пачка одновременных обновлений визуально шумит).
 *
 * Правила:
 *   count === undefined && loading  → skeleton-точка 12×12.
 *   count === undefined && !loading → ничего (данных нет / ошибка).
 *   count <= 0                      → ничего (не захламляем sidebar пустыми нулями).
 *   count > max                     → "{max}+".
 *   иначе                           → само значение.
 *
 * См. план: docs/folder-counters/FOLDER_COUNTERS_IMPLEMENTATION_PLAN.md §10.5
 */
export const FolderCountBadge: React.FC<IFolderCountBadgeProps> = ({ count, loading, max = DEFAULT_MAX, className }) => {
    if (count === undefined) {
        if (!loading) return null;
        return (
            <span
                className={clsx('rf-folder-count-badge', 'rf-folder-count-badge--loading', className)}
                aria-hidden
            />
        );
    }
    if (count <= 0) return null;

    const safeMax = Number.isFinite(max) && max > 0 ? max : DEFAULT_MAX;
    const text = count > safeMax ? `${safeMax}+` : String(count);

    return (
        <span className={clsx('rf-folder-count-badge', className)} aria-label={`Всего: ${text}`}>
            {text}
        </span>
    );
};
